// Настройка MathLive под СТО и русский интерфейс: шрифты офлайн, без звуков, десятичная запятая, макросы
// \tg \ctg \arctg \sh \ch …, быстрые сокращения ввода, экранная клавиатура с русскими подписями,
// статическая отрисовка формул (греческие буквы - прямым шрифтом, как в экспорте по 4.6.1.10).
import { MathfieldElement, convertLatexToMarkup, convertAsciiMathToLatex, convertLatexToMathMl } from 'mathlive';
import { MACRO_FUNCS } from './latex';
import { setAsciiMathConverter } from './detect';

MathfieldElement.fontsDirectory = '/fonts/';
MathfieldElement.soundsDirectory = null;
try { (MathfieldElement as any).locale = 'ru'; } catch { /* нет локали - английские подписи меню */ }
try { (MathfieldElement as any).decimalSeparator = ','; } catch { /* старый MathLive */ }
setAsciiMathConverter((s) => convertAsciiMathToLatex(s));

/** Макросы функций, которых нет в стандартном LaTeX (русская запись): \tg, \ctg, \arctg, \sh, \ch … */
export const MACROS: Record<string, any> = Object.fromEntries(
  MACRO_FUNCS.map((f) => [f, { def: '\\operatorname{' + f + '}', args: 0, captureSelection: false, expand: false }]));

/** Быстрые сокращения (поверх стандартных MathLive: alpha, sqrt, sum, int, >=, ->, oo …). */
export const SHORTCUTS: Record<string, string> = {
  tg: '\\tg', ctg: '\\ctg', arctg: '\\arctg', arcctg: '\\arcctg', lg: '\\lg', ln: '\\ln', sgn: '\\sgn',
  const: '\\mathrm{const}', '*': '\\cdot', xx: '\\times', '+-': '\\pm', '-+': '\\mp', '...': '\\ldots',
  '<=': '\\le', '>=': '\\ge', '!=': '\\ne', '~=': '\\approx', '==': '\\equiv', '->': '\\to', '=>': '\\Rightarrow',
  '<=>': '\\Leftrightarrow', '<->': '\\leftrightarrow', oo: '\\infty', grad: '\\grad', degree: '^{\\circ}',
  dx: '\\mathrm{d}x', dt: '\\mathrm{d}t', dy: '\\mathrm{d}y', deltax: '\\Delta x', ee: '\\mathrm{e}',
};

/** Применить настройки к полю формулы. */
export function setupField(mf: MathfieldElement) {
  const m = mf as any;
  m.mathVirtualKeyboardPolicy = 'manual';
  m.smartFence = true;
  m.smartMode = false;
  m.smartSuperscript = true;
  m.removeExtraneousParentheses = true;
  m.letterShapeStyle = 'tex';
  try { m.macros = { ...m.macros, ...MACROS }; } catch { /* */ }
  try { m.inlineShortcuts = { ...m.inlineShortcuts, ...SHORTCUTS }; } catch { /* */ }
  try { m.scriptDepth = [4, 4]; } catch { /* */ }
  // своё контекстное меню поля (правый щелчок) - по-русски и только нужное для отчёта
  try { m.menuItems = russianMenu(mf); } catch { /* */ }
  // греческие строчные - прямым шрифтом и при наборе (как в Word по 4.6.1.10)
  try {
    const root = mf.shadowRoot;
    if (root && !root.querySelector('style[data-sto]')) {
      const st = document.createElement('style');
      st.setAttribute('data-sto', '');
      st.textContent = '.lcGreek.ML__mathit{font-family:KaTeX_Main;font-style:normal}';
      root.appendChild(st);
    }
  } catch { /* */ }
}

/** Контекстное меню поля формулы (вместо английского меню MathLive). */
function russianMenu(mf: MathfieldElement): any[] {
  const cmd = (c: any) => () => { mf.executeCommand(c); mf.focus(); };
  const ins = (t: string) => cmd(['insert', t, { selectionMode: 'placeholder' }]);
  const mat = (r: number, c: number, env = 'pmatrix') =>
    '\\begin{' + env + '}' + Array.from({ length: r }, () => Array.from({ length: c }, () => '#?').join('&')).join('\\\\') + '\\end{' + env + '}';
  return [
    { type: 'submenu', label: 'Вставить матрицу', submenu: [
      { label: '2 × 2', onMenuSelect: ins(mat(2, 2)) }, { label: '3 × 3', onMenuSelect: ins(mat(3, 3)) },
      { label: '2 × 3', onMenuSelect: ins(mat(2, 3)) }, { label: '3 × 1 (столбец)', onMenuSelect: ins(mat(3, 1)) },
      { label: 'Определитель 2 × 2', onMenuSelect: ins(mat(2, 2, 'vmatrix')) }, { label: 'Определитель 3 × 3', onMenuSelect: ins(mat(3, 3, 'vmatrix')) },
    ] },
    { type: 'submenu', label: 'Строки и столбцы', submenu: [
      { label: 'Строка ниже', onMenuSelect: cmd('addRowAfter') }, { label: 'Строка выше', onMenuSelect: cmd('addRowBefore') },
      { label: 'Столбец справа', onMenuSelect: cmd('addColumnAfter') }, { label: 'Столбец слева', onMenuSelect: cmd('addColumnBefore') },
      { type: 'divider' },
      { label: 'Удалить строку', onMenuSelect: cmd('removeRow') }, { label: 'Удалить столбец', onMenuSelect: cmd('removeColumn') },
    ] },
    { label: 'Система уравнений', onMenuSelect: ins('\\begin{cases}#?\\\\#?\\end{cases}') },
    { type: 'divider' },
    { label: 'Вырезать', keyboardShortcut: 'meta+X', onMenuSelect: cmd('cutToClipboard') },
    { label: 'Копировать', keyboardShortcut: 'meta+C', onMenuSelect: cmd('copyToClipboard') },
    { label: 'Вставить', keyboardShortcut: 'meta+V', onMenuSelect: cmd('pasteFromClipboard') },
    { label: 'Выделить всё', keyboardShortcut: 'meta+A', onMenuSelect: cmd('selectAll') },
    { type: 'divider' },
    { label: 'Экранная клавиатура', onMenuSelect: () => toggleKeyboard(mf) },
  ];
}

// ------------------------------------------------------------------------------------------------ отрисовка
const GREEK_RE = /[α-ωϑϕϵϰϱϖς]/;

/** «Высокие» конструкции (дробь, сумма, интеграл, матрица): только вокруг них Word растягивает скобки. */
export const TALL_RE = /\\(?:d|t|c)?frac|\\over\b|\\sum|\\prod|\\int|\\oint|\\binom|\\begin\{|\\lim\b|\\substack|\\overset|\\underset/;

const DELIM_RE = /^(?:\\\{|\\\}|\\langle|\\rangle|\\lvert|\\rvert|\\lVert|\\rVert|\\\||[()[\]|.])/;

/** \left( … \right) без высоких конструкций внутри - обычные скобки: Word (OMML) не растягивает скобки вокруг
 *  «плоского» содержимого (h(t) = k(1 − e^{−t/T})), а MathLive растягивает их по индексу - формула выходила выше,
 *  чем в DOCX. */
export function flatDelims(src: string): string {
  if (src.indexOf('\\left') < 0) return src;
  let out = src;
  let from = 0;
  for (let guard = 0; guard < 60; guard++) {
    const i = out.indexOf('\\left', from);
    if (i < 0) break;
    const after = out.slice(i + 5);
    const lead = after.length - after.replace(/^\s+/, '').length;
    const m = DELIM_RE.exec(after.slice(lead));
    if (!m) { from = i + 5; continue; }
    const openEnd = i + 5 + lead + m[0].length;
    let depth = 1;
    let j = openEnd;
    let close = -1;
    while (j < out.length) {
      if (out.startsWith('\\left', j) && !/[a-zA-Z]/.test(out[j + 5] || '')) { depth++; j += 5; continue; }
      if (out.startsWith('\\right', j) && !/[a-zA-Z]/.test(out[j + 6] || '')) { depth--; if (depth === 0) { close = j; break; } j += 6; continue; }
      j++;
    }
    if (close < 0) break;
    const tail = out.slice(close + 6);
    const lead2 = tail.length - tail.replace(/^\s+/, '').length;
    const rm = DELIM_RE.exec(tail.slice(lead2));
    if (!rm) break;
    const inner = out.slice(openEnd, close);
    if (TALL_RE.test(inner)) { from = openEnd; continue; }          // высокое содержимое - скобки растягиваются
    const L = m[0] === '.' ? '' : m[0];
    const R = rm[0] === '.' ? '' : rm[0];
    out = out.slice(0, i) + L + inner + R + out.slice(close + 6 + lead2 + rm[0].length);
    from = i;
  }
  return out;
}

/** Статическая HTML-разметка формулы (MathLive) с прямыми греческими буквами - как в DOCX. */
export function renderTex(latex: string, display = false): string {
  const src = latex && latex.trim() ? flatDelims(latex) : '\\placeholder{}';
  let html: string;
  try {
    html = convertLatexToMarkup(src, { defaultMode: display ? 'math' : 'inline-math', macros: MACROS } as any);
  } catch {
    return '<span class="sto-math-err">' + escHtml(latex) + '</span>';
  }
  // греческие строчные MathLive рисует курсивом (как TeX) - в СТО они прямые (4.6.1.10)
  return html.replace(/<span class="(lcGreek |ucGreek )?ML__mathit"([^>]*)>([^<]*)<\/span>/g, (m, g, attrs, text) =>
    (g || GREEK_RE.test(text) ? `<span class="${g || ''}ML__cmr sto-greek"${attrs}>${text}</span>` : m));
}

export function escHtml(s: string) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** LaTeX -> MathML (для копирования в Word). */
export function toMathML(latex: string, display = true): string {
  let body = '';
  try { body = convertLatexToMathMl(latex); } catch { body = '<mi>?</mi>'; }
  return `<math xmlns="http://www.w3.org/1998/Math/MathML"${display ? ' display="block"' : ''}>${body}</math>`;
}

// ------------------------------------------------------------------------------------------------ клавиатура
const k = (latex: string, extra: Record<string, unknown> = {}) => ({ latex, ...extra });
/** Клавиша: вставляет insert, на клавише - label (текст) или формула (если label начинается с «$»). */
const ins = (insert: string, label: string, extra: Record<string, unknown> = {}) =>
  (label.startsWith('$') ? { insert, latex: label.slice(1), ...extra } : { insert, label, ...extra });

/** Экранная клавиатура: вкладки с русскими подписями. */
export const KEYBOARD_LAYOUTS = [
  {
    label: '123', tooltip: 'Числа и действия',
    rows: [
      [k('x'), k('y'), k('t'), '[separator-5]', '7', '8', '9', ins('\\frac{#@}{#?}', '$\\frac{\\square}{\\square}', { tooltip: 'Дробь' }), ins('#@^{#?}', '$x^{\\square}', { tooltip: 'Степень' }), ins('\\sqrt{#0}', '$\\sqrt{\\square}', { tooltip: 'Корень' })],
      [k('a'), k('b'), k('c'), '[separator-5]', '4', '5', '6', k('\\cdot', { tooltip: 'Умножение' }), ins('#@_{#?}', '$x_{\\square}', { tooltip: 'Индекс' }), ins('\\sqrt[#?]{#0}', '$\\sqrt[n]{\\square}', { tooltip: 'Корень степени n' })],
      [k('\\pi'), ins('\\mathrm{e}', 'e', { tooltip: 'Число e (прямым шрифтом)' }), k('\\infty'), '[separator-5]', '1', '2', '3', '-', '+', '='],
      [ins('\\left(#0\\right)', '( )', { tooltip: 'Скобки' }), ins('\\left|#0\\right|', '| |', { tooltip: 'Модуль' }), k('\\pm'), '[separator-5]', '0', ins('{,}', ',', { tooltip: 'Десятичная запятая' }), k('\\times'), '[left]', '[right]', '[backspace]'],
    ],
  },
  {
    label: 'f(x)', tooltip: 'Функции, пределы, суммы, интегралы',
    rows: [
      [k('\\sin'), k('\\cos'), k('\\tg'), k('\\ctg'), '[separator-5]', ins('\\lim_{#?\\to #?}', 'lim', { tooltip: 'Предел' }), ins('\\sum_{#?}^{#?}', '∑', { tooltip: 'Сумма' }), ins('\\prod_{#?}^{#?}', '∏', { tooltip: 'Произведение' }), ins('\\int_{#?}^{#?}', '∫', { tooltip: 'Интеграл с пределами' }), ins('\\,\\mathrm{d}#?', 'dx', { tooltip: 'Дифференциал' })],
      [k('\\arcsin'), k('\\arccos'), k('\\arctg'), k('\\arcctg'), '[separator-5]', k('\\ln'), k('\\lg'), ins('\\log_{#?}', '$\\log_{a}', { tooltip: 'Логарифм по основанию' }), ins('\\mathrm{e}^{#?}', '$\\mathrm{e}^{x}', { tooltip: 'Экспонента' }), k('\\exp')],
      [ins('\\frac{\\mathrm{d}#?}{\\mathrm{d}#?}', 'd/dx', { tooltip: 'Производная' }), ins('\\frac{\\partial #?}{\\partial #?}', '∂/∂x', { tooltip: 'Частная производная' }), ins("#@'", "x'", { tooltip: 'Штрих' }), ins('\\dot{#@}', 'ẋ', { tooltip: 'Точка (по времени)' }), '[separator-5]', k('\\max'), k('\\min'), k('\\sh'), k('\\ch'), k('\\th')],
      [k('\\Delta'), k('\\partial'), k('\\nabla'), ins('\\vec{#@}', 'x⃗', { tooltip: 'Вектор' }), ins('\\bar{#@}', 'x̄', { tooltip: 'Среднее' }), '[separator-5]', ins('\\sgn', 'sgn'), '[left]', '[right]', '[backspace]'],
    ],
  },
  {
    label: 'αβγ', tooltip: 'Греческие буквы (прямым шрифтом по 4.6.1.10)',
    rows: [
      ['\\alpha', '\\beta', '\\gamma', '\\delta', '\\varepsilon', '\\zeta', '\\eta', '\\theta', '\\lambda', '\\mu'].map((x) => k(x)),
      ['\\nu', '\\xi', '\\pi', '\\rho', '\\sigma', '\\tau', '\\varphi', '\\chi', '\\psi', '\\omega'].map((x) => k(x)),
      ['\\Gamma', '\\Delta', '\\Theta', '\\Lambda', '\\Xi', '\\Pi', '\\Sigma', '\\Phi', '\\Psi', '\\Omega'].map((x) => k(x)),
      [k('\\epsilon'), k('\\vartheta'), k('\\phi'), k('\\kappa'), k('\\iota'), k('\\upsilon'), '[shift]', '[left]', '[right]', '[backspace]'],
    ],
  },
  {
    label: '≤⇒∈', tooltip: 'Знаки: соотношения, стрелки, логика, множества',
    rows: [
      ['<', '>', '\\le', '\\ge', '\\ne', '\\approx', '\\equiv', '\\sim', '\\propto', '\\mp'].map((x) => k(x)),
      ['\\to', '\\leftarrow', '\\leftrightarrow', '\\Rightarrow', '\\Leftrightarrow', '\\rightleftarrows', '\\uparrow', '\\downarrow', '\\mapsto', '\\ldots'].map((x) => k(x)),
      ['\\in', '\\notin', '\\subset', '\\subseteq', '\\cup', '\\cap', '\\setminus', '\\emptyset', '\\forall', '\\exists'].map((x) => k(x)),
      [k('\\neg'), k('\\land'), k('\\lor'), k('\\parallel'), k('\\perp'), k('\\angle'), ins('^{\\circ}', '°', { tooltip: 'Градус' }), '[left]', '[right]', '[backspace]'],
    ],
  },
  {
    label: '[▦]', tooltip: 'Матрицы, определители, системы',
    rows: [
      [ins('\\begin{pmatrix}#?&#?\\\\#?&#?\\end{pmatrix}', '(2×2)', { tooltip: 'Матрица 2×2' }), ins('\\begin{pmatrix}#?&#?&#?\\\\#?&#?&#?\\\\#?&#?&#?\\end{pmatrix}', '(3×3)', { tooltip: 'Матрица 3×3' }), ins('\\begin{vmatrix}#?&#?\\\\#?&#?\\end{vmatrix}', '|2×2|', { tooltip: 'Определитель 2×2' }), ins('\\begin{vmatrix}#?&#?&#?\\\\#?&#?&#?\\\\#?&#?&#?\\end{vmatrix}', '|3×3|', { tooltip: 'Определитель 3×3' }), ins('\\begin{pmatrix}#?\\\\#?\\\\#?\\end{pmatrix}', '(▫)ᵀ', { tooltip: 'Вектор-столбец' })],
      [ins('\\begin{cases}#?\\\\#?\\end{cases}', '{ 2', { tooltip: 'Система из двух уравнений' }), ins('\\begin{cases}#?\\\\#?\\\\#?\\end{cases}', '{ 3', { tooltip: 'Система из трёх уравнений' }), ins('\\begin{cases}#?, & #?\\\\#?, & #?\\end{cases}', '{ ,если', { tooltip: 'Кусочная функция' }), ins('\\begin{bmatrix}#?&#?\\\\#?&#?\\end{bmatrix}', '[2×2]', { tooltip: 'Матрица в квадратных скобках' })],
      [{ label: '+ строка', command: 'addRowAfter', class: 'small action', tooltip: 'Добавить строку ниже' }, { label: '+ столбец', command: 'addColumnAfter', class: 'small action', tooltip: 'Добавить столбец справа' }, { label: '− строка', command: 'removeRow', class: 'small action', tooltip: 'Удалить строку' }, { label: '− столбец', command: 'removeColumn', class: 'small action', tooltip: 'Удалить столбец' }, '[left]', '[right]', '[backspace]'],
    ],
  },
  'alphabetic',
];

let kbReady = false;
/** Настроить глобальную экранную клавиатуру MathLive (один раз). */
export function setupKeyboard() {
  if (kbReady) return;
  const kb = (window as any).mathVirtualKeyboard;
  if (!kb) return;
  try { kb.layouts = KEYBOARD_LAYOUTS; kbReady = true; } catch { /* */ }
}

/** Показать/скрыть экранную клавиатуру для поля. */
export function toggleKeyboard(mf?: MathfieldElement | null) {
  setupKeyboard();
  const kb = (window as any).mathVirtualKeyboard;
  if (!kb) return;
  if (kb.visible) { kb.hide(); document.body.classList.remove('mx-kb-open'); return; }
  mf?.focus();
  kb.show({ animate: true });
  // клавиатура закрывает низ окна: место под документом и формула - в видимой части
  document.body.classList.add('mx-kb-open');
  setTimeout(() => mf?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250);
  kb.addEventListener?.('geometrychange', () => { if (!kb.visible) document.body.classList.remove('mx-kb-open'); }, { once: true });
}
